import { useState, useEffect, useRef } from 'react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { DecisionPortal } from '@/components/onboarding/DecisionPortal';
import { StepDecisionSubtype } from '@/components/onboarding/StepDecisionSubtype';
import { StepSituation } from '@/components/onboarding/StepSituation';
import { StepAnalysisPreview } from '@/components/onboarding/StepAnalysisPreview';
import { useOnboardingStore } from '@/stores/onboardingStore';
import { SeatPersonalization } from '@/hooks/useSeatPersonalization';
import { Sparkles, ArrowLeft, Target, MessageSquare, Rocket } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface NewDecisionSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  personalization: SeatPersonalization;
  firstName?: string | null;
}

const STEP_CONFIG = [
  { icon: Sparkles, label: 'Bereich', color: 'from-primary/20 to-primary/5' },
  { icon: Target, label: 'Typ', color: 'from-amber-500/20 to-amber-500/5' },
  { icon: MessageSquare, label: 'Situation', color: 'from-emerald-500/20 to-emerald-500/5' },
  { icon: Rocket, label: 'Analyse', color: 'from-blue-500/20 to-blue-500/5' },
];

export const NewDecisionSheet = ({
  open,
  onOpenChange,
  workspaceId,
  personalization,
  firstName,
}: NewDecisionSheetProps) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [direction, setDirection] = useState<'forward' | 'backward'>('forward');
  const [isAnimating, setIsAnimating] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  
  const {
    decisionType,
    decisionSubtype,
    trigger,
    stakes,
    previousAttempts,
    impactScore,
    urgencyScore,
    setDecisionArea,
    setDecisionSubtype: setStoreDecisionSubtype,
    setSubStep,
    reset,
  } = useOnboardingStore();

  // Reset store when sheet opens to ensure fresh state
  useEffect(() => {
    if (open) {
      reset();
      setCurrentStep(1);
      setDirection('forward');
    }
  }, [open, reset]);

  // Trigger animation on step change
  useEffect(() => {
    setIsAnimating(true);
    const timer = setTimeout(() => setIsAnimating(false), 400);
    return () => clearTimeout(timer);
  }, [currentStep]);

  // Check if user has entered any data
  const hasUnsavedData = currentStep > 1 || decisionType || trigger || stakes || previousAttempts;

  const handleCloseRequest = (open: boolean) => {
    if (!open && hasUnsavedData) {
      setShowCloseConfirm(true);
    } else if (!open) {
      reset();
      setCurrentStep(1);
      onOpenChange(false);
    } else {
      onOpenChange(open);
    }
  };

  const handleConfirmClose = () => {
    setShowCloseConfirm(false);
    reset();
    setCurrentStep(1);
    onOpenChange(false);
  };

  const handlePortalDecisionStart = (type: string) => {
    setDirection('forward');
    setDecisionArea(type);
    setCurrentStep(2);
  };

  const handleDecisionSubtypeComplete = (subtype: string) => {
    setDirection('forward');
    setStoreDecisionSubtype(subtype);
    setCurrentStep(3);
  };

  const handleSituationComplete = () => {
    setDirection('forward');
    setCurrentStep(4);
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setDirection('backward');
      setCurrentStep(currentStep - 1);
    }
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case 1:
        return 'Entscheidungsbereich wählen';
      case 2:
        return 'Entscheidungstyp konkretisieren';
      case 3:
        return 'Situation beschreiben';
      case 4:
        return 'Analyse starten';
      default:
        return 'Neue Entscheidung';
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <DecisionPortal
            personalization={personalization}
            firstName={firstName}
            onStartDecision={handlePortalDecisionStart}
          />
        );
      case 2:
        return decisionType ? (
          <StepDecisionSubtype
            decisionType={decisionType}
            onComplete={handleDecisionSubtypeComplete}
          />
        ) : null;
      case 3:
        return decisionType && decisionSubtype ? (
          <StepSituation
            workspaceId={workspaceId}
            role={personalization.job_role || ''}
            title={personalization.job_title || undefined}
            decisionType={decisionType}
            decisionSubtype={decisionSubtype}
            onComplete={handleSituationComplete}
          />
        ) : null;
      case 4:
        return workspaceId && decisionType && decisionSubtype && trigger ? (
          <StepAnalysisPreview
            workspaceId={workspaceId}
            role={personalization.job_role || ''}
            title={personalization.job_title || undefined}
            decisionType={decisionType}
            decisionSubtype={decisionSubtype}
            trigger={trigger}
            stakes={stakes}
            previousAttempts={previousAttempts}
            impactScore={impactScore}
            urgencyScore={urgencyScore}
            onEdit={() => {
              setDirection('backward');
              setSubStep(1);
              setCurrentStep(3);
            }}
          />
        ) : null;
      default:
        return null;
    }
  };

  const CurrentStepIcon = STEP_CONFIG[currentStep - 1]?.icon || Sparkles;

  // Animation classes based on direction
  const getAnimationClass = () => {
    if (!isAnimating) return 'opacity-100 translate-x-0';
    return direction === 'forward' 
      ? 'animate-slide-in-from-right' 
      : 'animate-slide-in-from-left';
  };

  return (
    <>
      <Sheet open={open} onOpenChange={handleCloseRequest}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto border-border/50 bg-background p-0">
          {/* Background decorative elements */}
          <div className="fixed top-0 right-0 w-96 h-96 bg-gradient-to-br from-primary/5 to-transparent rounded-full blur-3xl pointer-events-none" />
          <div className="fixed bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-primary/3 to-transparent rounded-full blur-2xl pointer-events-none" />
          
          {/* Subtle grid pattern */}
          <div 
            className="fixed inset-0 opacity-[0.02] pointer-events-none"
            style={{
              backgroundImage: `linear-gradient(hsl(var(--primary)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--primary)) 1px, transparent 1px)`,
              backgroundSize: '60px 60px',
            }}
          />
          
          <div className="relative z-10">
            {/* Premium Header */}
            <div className="sticky top-0 z-20 bg-background/95 backdrop-blur-sm border-b border-border/50">
              <div className="px-6 py-4">
                <div className="flex items-center gap-4">
                  {currentStep > 1 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleBack}
                      className="h-10 w-10 rounded-xl hover:bg-primary/10 hover:text-primary transition-all shrink-0"
                    >
                      <ArrowLeft className="h-5 w-5" />
                    </Button>
                  )}
                  <div className={cn(
                    "w-11 h-11 rounded-xl flex items-center justify-center shrink-0 transition-all duration-500",
                    `bg-gradient-to-br ${STEP_CONFIG[currentStep - 1]?.color || 'from-primary/20 to-primary/5'}`
                  )}>
                    <CurrentStepIcon className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <SheetTitle className="text-lg font-semibold truncate transition-all duration-300">{getStepTitle()}</SheetTitle>
                    <SheetDescription className="text-sm text-muted-foreground">
                      Strukturierter Entscheidungsprozess
                    </SheetDescription>
                  </div>
                </div>
                
                {/* Progress indicator with fade-in animation */}
                <div className="flex gap-2 mt-4 animate-fade-in">
                  {STEP_CONFIG.map((step, index) => {
                    const stepNum = index + 1;
                    const isActive = stepNum === currentStep;
                    const isCompleted = stepNum < currentStep;
                    
                    return (
                      <div
                        key={index}
                        className="flex-1 relative"
                        style={{ 
                          animationDelay: `${index * 100}ms`,
                          animationFillMode: 'both'
                        }}
                      >
                        <div className={cn(
                          "h-1.5 rounded-full transition-all duration-500 animate-fade-in",
                          isCompleted ? "bg-primary" : isActive ? "bg-primary shadow-[0_0_10px_hsl(var(--primary)/0.5)]" : "bg-muted/50"
                        )} 
                        style={{ 
                          animationDelay: `${index * 100 + 150}ms`,
                          animationFillMode: 'both'
                        }}
                        />
                        <span className={cn(
                          "absolute -bottom-5 left-1/2 -translate-x-1/2 text-[10px] font-medium transition-colors duration-300 whitespace-nowrap animate-fade-in",
                          isActive ? "text-primary" : isCompleted ? "text-foreground/60" : "text-muted-foreground/40"
                        )}
                        style={{ 
                          animationDelay: `${index * 100 + 200}ms`,
                          animationFillMode: 'both'
                        }}
                        >
                          {step.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              {/* Subtle divider */}
              <div className="mt-8 border-b border-border/50" />
            </div>
            
            {/* Content with animated transitions */}
            <div className="px-6 py-8 pt-12 overflow-hidden">
              <div 
                ref={contentRef}
                key={currentStep}
                className={cn(
                  "transition-all duration-400 ease-out",
                  getAnimationClass()
                )}
              >
                {renderStep()}
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <AlertDialog open={showCloseConfirm} onOpenChange={setShowCloseConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eingaben verwerfen?</AlertDialogTitle>
            <AlertDialogDescription>
              Du hast bereits Daten eingegeben. Wenn du jetzt schließt, gehen alle Eingaben verloren.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Zurück</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmClose} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Verwerfen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
